import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import BaseLayout from './BaseLayout';
import VideoModal from './ExerciseVideoModal';
import { buildApiUrl } from '../config/api';
import { exerciseVideoLink } from './video';
import {
    EMPTY_FORM,
    buildExerciseRequest,
    buildExerciseUpdateRequest,
    formFromEntry,
    apiErrorMessage,
} from './prescriptionForm';
import { repsPrescribed } from './repScheme';
import { loadLabel } from './units';
import { usePreferences } from './usePreferences';
import ProgramPhases from './ProgramPhases';
import { WEEKDAYS } from './schedule';
import ProgramDrafts from './ProgramDrafts';
import ProgramPdfButton from './ProgramPdfButton';
import '../styles/EditProgram.css';

// The train.css and performance-logger.css imports were removed. CSS is global in
// this app, so importing train.css here leaked its `.exercise-card`,
// `.exercise-name` and `.demo-button` rules onto the exercise picker, which uses
// the same class names for a completely different component. The picker classes
// are now namespaced (`picker-*`) so the collision cannot recur.

const EditProgram = () => {
    const { programId } = useParams();
    const { token } = useAuth();
    // Loads are entered and shown in the coach's own unit; the bounds come with
    // it, already expressed in that unit. The client never converts.
    const { unit, loadRange } = usePreferences();

    const [program, setProgram] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [currentSession, setCurrentSession] = useState(null);
    const [isAddingSession, setIsAddingSession] = useState(false);

    // Exercise selection
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [filteredExercises, setFilteredExercises] = useState([]);

    // Whether the last search matched more than the API will return. The server
    // caps a search, so a full-looking list of results can be missing matches —
    // "squat" alone matches 229 exercises in the shipped library. Saying so is
    // what keeps the cap from quietly hiding the exercise a coach is looking
    // for.
    const [searchTruncated, setSearchTruncated] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedExercise, setSelectedExercise] = useState(null);
    const [showExerciseForm, setShowExerciseForm] = useState(false);
    // The prescription being edited, or null when adding a new one. Editing and
    // adding share the whole form; only the exercise picker and the verb differ.
    const [editingExercise, setEditingExercise] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [exerciseEntry, setExerciseEntry] = useState(EMPTY_FORM);
    // Field-level validation errors, keyed by field name.
    const [fieldErrors, setFieldErrors] = useState({});

    const [deletingExercise, setDeletingExercise] = useState(null);
    const [selectedVideo, setSelectedVideo] = useState(null);

    // Page-level feedback. { tone: 'success' | 'error', message }
    const [notice, setNotice] = useState(null);
    const [phases, setPhases] = useState([]);
    const [isSubmittingPhase, setIsSubmittingPhase] = useState(false);

    const authConfig = useCallback(
        () => ({ headers: { Authorization: `Bearer ${token}` } }),
        [token]
    );

    const fetchProgram = useCallback(async () => {
        try {
            const response = await axios.get(
                buildApiUrl(`/programs/${programId}`),
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setProgram(response.data);
            setLoadError(null);
            return response.data;
        } catch (error) {
            // A load failure used to be a console.error, leaving the screen stuck
            // on "Loading program..." forever.
            setLoadError(apiErrorMessage(error, 'Could not load this program.'));
            return null;
        }
    }, [programId, token]);

    // The programme's blocks. Fetched separately from the programme itself
    // because a block is a grouping over sessions, not part of the session tree —
    // and a failure here must not blank the designer.
    const fetchPhases = useCallback(async () => {
        try {
            const response = await axios.get(
                buildApiUrl(`/programs/${programId}/phases`),
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPhases(response.data?.phases || []);
        } catch (error) {
            setPhases([]);
        }
    }, [programId, token]);

    /**
     * Create a block. Returns true on success so the form knows whether to close.
     */
    const handleCreatePhase = async (body) => {
        setIsSubmittingPhase(true);
        setNotice(null);
        try {
            await axios.post(buildApiUrl(`/programs/${programId}/phases`), body, authConfig());
            await fetchPhases();
            setNotice({ tone: 'success', message: 'Block added.' });
            return true;
        } catch (error) {
            setNotice({
                tone: 'error',
                message: apiErrorMessage(error, 'Could not add the block.'),
            });
            return false;
        } finally {
            setIsSubmittingPhase(false);
        }
    };

    /**
     * Attach a session to a block, or detach it.
     *
     * PUT is a full replace, so the name and order have to go with it — omitting
     * either would clear it. An empty selection sends no phaseId at all, which is
     * how "take this session out of the block" is expressed.
     */
    const handleSessionPhaseChange = (session, phaseId) =>
        updateSession(session, { phaseId, dayOfWeek: session.dayOfWeek });

    /**
     * Put a session on a weekday, or take it off the calendar.
     *
     * A weekday is what turns a dateless block into dates: combined with an
     * assignment's start it gives every session a real day, computed per athlete
     * and never stored. A session with no weekday simply has no calendar date,
     * which is a normal state and not an incomplete one.
     */
    const handleSessionDayChange = (session, dayOfWeek) =>
        updateSession(session, {
            phaseId: session.phaseId,
            dayOfWeek: dayOfWeek === '' ? null : Number(dayOfWeek),
        });

    /**
     * PUT is a full replace, so every field has to go with every change —
     * omitting the phase while setting the weekday would detach the session, and
     * vice versa. One helper so that cannot be got wrong in one of two places.
     */
    const updateSession = async (session, { phaseId, dayOfWeek }) => {
        setNotice(null);
        try {
            await axios.put(
                buildApiUrl(`/programs/${programId}/sessions/${session.id}`),
                {
                    name: session.name,
                    order: session.order,
                    ...(phaseId ? { phaseId: Number(phaseId) } : {}),
                    ...(dayOfWeek === null || dayOfWeek === undefined
                        ? {} : { dayOfWeek: Number(dayOfWeek) }),
                },
                authConfig()
            );
            await Promise.all([fetchProgram(), fetchPhases()]);
        } catch (error) {
            setNotice({
                tone: 'error',
                message: apiErrorMessage(error, 'Could not move that session.'),
            });
        }
    };

    const fetchCategories = useCallback(async () => {
        try {
            const response = await axios.get(
                buildApiUrl('/exercises/category'),
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setCategories(response.data || []);
        } catch (error) {
            setCategories([]);
        }
    }, [token]);

    useEffect(() => {
        if (programId) {
            fetchProgram();
            fetchCategories();
            fetchPhases();
        }
    }, [programId, fetchProgram, fetchCategories, fetchPhases]);

    /**
     * Add a training session to this program.
     *
     * This posted to /programs/:id/sessions/initialize, which matched no route.
     * The 404 went to console.error, so the button looked functional and a
     * program with no sessions was a permanent dead end. The endpoint now exists
     * as POST /programs/:id/sessions.
     */
    const handleAddSession = async () => {
        setIsAddingSession(true);
        setNotice(null);
        try {
            await axios.post(
                buildApiUrl(`/programs/${programId}/sessions`),
                {},
                authConfig()
            );
            await fetchProgram();
            setNotice({ tone: 'success', message: 'Session added.' });
        } catch (error) {
            setNotice({
                tone: 'error',
                message: apiErrorMessage(error, 'Could not add a session.'),
            });
        } finally {
            setIsAddingSession(false);
        }
    };

    const loadExercisesForCategory = async (category) => {
        if (!category) {
            setFilteredExercises([]);
            setSearchTruncated(false);
            return;
        }
        try {
            const response = await axios.get(
                buildApiUrl(`/exercises?category=${encodeURIComponent(category)}`),
                authConfig()
            );
            setFilteredExercises(response.data || []);
            setSearchTruncated(false);
        } catch (error) {
            setFilteredExercises([]);
            setSearchTruncated(false);
            setNotice({
                tone: 'error',
                message: apiErrorMessage(error, 'Could not load exercises.'),
            });
        }
    };

    const handleCategoryChange = async (e) => {
        const category = e.target.value;
        setSelectedCategory(category);
        setSearchTerm('');
        await loadExercisesForCategory(category);
    };

    const handleSearchChange = async (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        // Clearing the box used to leave the previous results on screen with no
        // indication they no longer matched. Fall back to the selected category.
        if (value.trim().length < 2) {
            await loadExercisesForCategory(selectedCategory);
            return;
        }

        try {
            const response = await axios.get(
                buildApiUrl(`/exercises/search?q=${encodeURIComponent(value.trim())}`),
                authConfig()
            );
            setFilteredExercises(response.data || []);
            // Optional-chained because the header is absent on a complete
            // answer, and absent entirely across origins that do not expose it.
            // Both mean "no reason to think anything is missing".
            setSearchTruncated(response.headers?.['x-search-truncated'] === 'true');
        } catch (error) {
            setSearchTruncated(false);
            setNotice({
                tone: 'error',
                message: apiErrorMessage(error, 'Search failed.'),
            });
        }
    };

    const handleExerciseSelect = (exercise) => {
        setSelectedExercise(exercise);
        setFieldErrors(({ exercise: _dropped, ...rest }) => rest);
    };

    const handleEntryChange = (e) => {
        const { name, value } = e.target;
        setExerciseEntry(prev => ({ ...prev, [name]: value }));
        // Clear this field's error as soon as it is edited.
        setFieldErrors(prev => {
            if (!(name in prev)) return prev;
            const { [name]: _dropped, ...rest } = prev;
            return rest;
        });
    };

    const closeExerciseForm = () => {
        setEditingExercise(null);
        setShowExerciseForm(false);
        setSelectedExercise(null);
        setExerciseEntry(EMPTY_FORM);
        setFieldErrors({});
        setSearchTerm('');
    };

    /**
     * Add the configured exercise to the current session.
     *
     * The prescription is validated client-side first, mirroring the backend
     * domain rules, and any server error is surfaced verbatim. Previously an
     * invalid prescription produced a 400 into console.error while the modal sat
     * there looking functional — a regression risk created when the backend
     * gained strict prescription validation.
     */
    const handleExerciseSubmit = async () => {
        if (!currentSession) return;

        const isEditing = Boolean(editingExercise);
        const { body, errors } = isEditing
            ? buildExerciseUpdateRequest(exerciseEntry, unit, loadRange)
            : buildExerciseRequest(exerciseEntry, selectedExercise, unit, loadRange);
        if (errors) {
            setFieldErrors(errors);
            return;
        }

        setIsSubmitting(true);
        setFieldErrors({});
        try {
            if (isEditing) {
                // PUT is a full replace: the body carries the whole prescription,
                // so an intensity field the coach cleared is cleared server-side.
                await axios.put(
                    buildApiUrl(
                        `/programs/${programId}/sessions/${currentSession.id}/exercises/${editingExercise.id}`
                    ),
                    body,
                    authConfig()
                );
            } else {
                await axios.post(
                    buildApiUrl(`/programs/${programId}/sessions/${currentSession.id}/exercises`),
                    body,
                    authConfig()
                );
            }
            // Refetch rather than splicing the response into state, so what is
            // shown is what the server stored.
            await fetchProgram();
            closeExerciseForm();
            setNotice({
                tone: 'success',
                message: isEditing
                    ? `Updated ${editingExercise.exerciseName}.`
                    : `Added ${body.exerciseName} to ${currentSession.name}.`,
            });
        } catch (error) {
            setFieldErrors({
                form: apiErrorMessage(
                    error,
                    isEditing ? 'Could not save the change.' : 'Could not add the exercise.'
                ),
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    /**
     * Open the prescription form on an existing entry.
     *
     * The exercise picker stays hidden: changing which exercise a prescription is
     * for is not an edit, because the sets already logged against it would
     * silently reattach to a different movement. The backend refuses it too.
     */
    const handleEditExerciseClick = (exercise, session) => {
        setCurrentSession(session);
        setEditingExercise(exercise);
        setExerciseEntry(formFromEntry(exercise));
        setFieldErrors({});
        setShowExerciseForm(true);
        setNotice(null);
    };

    const handleAddExerciseClick = (session) => {
        setCurrentSession(session);
        setShowExerciseForm(true);
        setNotice(null);
        loadExercisesForCategory(selectedCategory);
    };

    const handleDeleteExercise = async (exercise, sessionId) => {
        const confirmed = window.confirm(`Delete "${exercise.exerciseName}" from this session?`);
        if (!confirmed) return;

        setDeletingExercise(exercise.id);
        setNotice(null);
        try {
            await axios.delete(
                buildApiUrl(`/programs/${programId}/sessions/${sessionId}/exercises/${exercise.id}`),
                authConfig()
            );
            await fetchProgram();
            setNotice({ tone: 'success', message: `Removed ${exercise.exerciseName}.` });
        } catch (error) {
            // A failed delete used to be silent, so the exercise appeared to
            // vanish (optimistic update) and then reappear on the next load.
            setNotice({
                tone: 'error',
                message: apiErrorMessage(error, 'Could not remove that exercise.'),
            });
        } finally {
            setDeletingExercise(null);
        }
    };

    const sessions = program?.sessions ?? [];

    if (loadError) {
        return (
            <BaseLayout>
                <div className="edit-program">
                    <div className="status-error">{loadError}</div>
                </div>
            </BaseLayout>
        );
    }

    if (!program) {
        return (
            <BaseLayout>
                <div className="edit-program">
                    <p className="body-copy">Loading program…</p>
                </div>
            </BaseLayout>
        );
    }

    return (
        <BaseLayout>
            <div className="edit-program">
                <header className="program-header">
                    <div className="program-header-text">
                        <span className="pill">Program designer</span>
                        <h1 className="page-title program-title">{program.name}</h1>
                        {program.description && (
                            <p className="body-copy program-description">{program.description}</p>
                        )}
                        <p className="meta-copy program-meta">
                            {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'}
                        </p>
                    </div>
                    <div className="program-header-actions">
                        <ProgramPdfButton programId={program.id} />
                        <button
                            className="btn-ghost add-session-btn"
                            onClick={handleAddSession}
                            disabled={isAddingSession}
                        >
                            {isAddingSession ? 'Adding…' : 'Add session'}
                        </button>
                    </div>
                </header>

                {notice && (
                    <div
                        className={notice.tone === 'error' ? 'status-error' : 'status-success'}
                        role="status"
                        aria-live="polite"
                    >
                        {notice.message}
                    </div>
                )}

                <ProgramPhases
                    programId={program.id}
                    phases={phases}
                    onCreate={handleCreatePhase}
                    isSubmitting={isSubmittingPhase}
                />

                <ProgramDrafts
                    programId={program.id}
                    exercises={filteredExercises}
                    onCommitted={() => {
                        fetchProgram();
                        fetchPhases();
                    }}
                />

                {sessions.length === 0 ? (
                    <div className="callout-panel empty-state">
                        <p className="empty-state-text">
                            This program has no sessions yet. Add one to start prescribing.
                        </p>
                    </div>
                ) : (
                    <div className="session-list">
                        {sessions.map((session, index) => (
                            <article className="card session-card" key={session.id}>
                                <div className="session-card-head">
                                    <div className="session-card-title">
                                        <span className="surface-badge">Day {index + 1}</span>
                                        <h2 className="section-title session-name">{session.name}</h2>
                                    </div>
                                    <div className="session-card-actions">
                                        {phases.length > 0 && (
                                            <label className="session-phase">
                                                {/* The label is for assistive technology only — the
                                                    select's own options say what it does, and every
                                                    session card repeating "Block for Day 1" is noise
                                                    on screen. */}
                                                <span className="sr-only">
                                                    Block for {session.name}
                                                </span>
                                                <select
                                                    value={session.phaseId ?? ''}
                                                    onChange={(e) =>
                                                        handleSessionPhaseChange(session, e.target.value)
                                                    }
                                                >
                                                    {/* Detaching has to be expressible: a session
                                                        outside a block is a normal state. */}
                                                    <option value="">No block</option>
                                                    {phases.map((phase) => (
                                                        <option key={phase.id} value={phase.id}>
                                                            {phase.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        )}
                                        {/* The weekday, which is what gives this
                                            session a date once an athlete is
                                            assigned the programme. Unlike the
                                            block picker this always shows: a
                                            session can have a day before any
                                            block exists. */}
                                        <label className="session-phase">
                                                <span className="sr-only">
                                                    Weekday for {session.name}
                                                </span>
                                                <select
                                                    value={session.dayOfWeek ?? ''}
                                                    onChange={(e) =>
                                                        handleSessionDayChange(session, e.target.value)
                                                    }
                                                >
                                                    <option value="">No day</option>
                                                    {WEEKDAYS.map((d) => (
                                                        <option key={d.value} value={d.value}>
                                                            {d.name}
                                                        </option>
                                                    ))}
                                                </select>
                                        </label>
                                        <button
                                            className="btn-ghost"
                                            onClick={() => handleAddExerciseClick(session)}
                                        >
                                            Add exercise
                                        </button>
                                        <Link
                                            to={`/programs/${program.id}/sessions/${session.id}/train`}
                                            className="btn-primary start-training-btn"
                                        >
                                            Start training
                                        </Link>
                                    </div>
                                </div>

                                {session.exercises?.length > 0 ? (
                                    <ul className="prescription-list">
                                        {session.exercises.map((exercise) => {
                                            const video = exerciseVideoLink(exercise);
                                            return (
                                                <li className="embed-shell prescription-row" key={exercise.id}>
                                                    <span className="prescription-series mono">
                                                        {exercise.series}
                                                    </span>
                                                    <span className="prescription-name">
                                                        {exercise.exerciseName}
                                                    </span>
                                                    <span className="prescription-volume mono">
                                                        {/* The scheme, not the legacy integer. Reading
                                                            `reps` here showed "4×8" for a prescription of
                                                            "8-10" and "3×11" for a wave of "11,9,7" — the
                                                            designer displaying something the coach never
                                                            wrote. */}
                                                        {exercise.sets}×{repsPrescribed(exercise)}
                                                        {exercise.tempo ? ` · ${exercise.tempo}` : ''}
                                                    </span>
                                                    <span className="prescription-row-actions">
                                                        {video && (
                                                            <button
                                                                className="btn-ghost demo-link"
                                                                onClick={() => setSelectedVideo(video)}
                                                            >
                                                                Demo
                                                            </button>
                                                        )}
                                                        <button
                                                            className="btn-ghost"
                                                            onClick={() =>
                                                                handleEditExerciseClick(exercise, session)
                                                            }
                                                            aria-label={`Edit ${exercise.exerciseName}`}
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            className="icon-button"
                                                            onClick={() =>
                                                                handleDeleteExercise(exercise, session.id)
                                                            }
                                                            disabled={deletingExercise === exercise.id}
                                                            aria-label={`Delete ${exercise.exerciseName}`}
                                                        >
                                                            {deletingExercise === exercise.id ? '…' : '✕'}
                                                        </button>
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : (
                                    <p className="meta-copy empty-session">
                                        No exercises in this session yet.
                                    </p>
                                )}
                            </article>
                        ))}
                    </div>
                )}
            </div>

            {showExerciseForm && currentSession && (
                <div className="modal-overlay" onClick={closeExerciseForm}>
                    <div
                        className="card exercise-form"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-label={
                            editingExercise
                                ? `Edit ${editingExercise.exerciseName}`
                                : `Add exercise to ${currentSession.name}`
                        }
                    >
                        <div className="exercise-form-head">
                            <h2 className="section-title">
                                {editingExercise ? 'Edit prescription' : 'Add exercise'}
                            </h2>
                            <p className="meta-copy">
                                {editingExercise ? editingExercise.exerciseName : currentSession.name}
                            </p>
                        </div>

                        {fieldErrors.form && <div className="status-error">{fieldErrors.form}</div>}

                        {!editingExercise && (
                        <div className="picker">
                            <div className="picker-controls">
                                <label className="field-label" htmlFor="exercise-search">
                                    Search exercises
                                    <input
                                        id="exercise-search"
                                        type="search"
                                        className="field"
                                        placeholder="e.g. squat"
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                    />
                                </label>
                                <label className="field-label" htmlFor="exercise-category">
                                    Category
                                    <select
                                        id="exercise-category"
                                        className="field"
                                        value={selectedCategory}
                                        onChange={handleCategoryChange}
                                    >
                                        <option value="">All categories</option>
                                        {categories.map((category) => (
                                            <option key={category} value={category}>
                                                {category}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>

                            {fieldErrors.exercise && (
                                <p className="field-error">{fieldErrors.exercise}</p>
                            )}

                            {searchTruncated && filteredExercises.length > 0 && (
                                <p className="meta-copy picker-truncated">
                                    Showing the first {filteredExercises.length} matches,
                                    alphabetically. Type more of the name to narrow it.
                                </p>
                            )}

                            {filteredExercises.length === 0 ? (
                                <p className="meta-copy picker-empty">
                                    Search by name, or pick a category, to find an exercise.
                                </p>
                            ) : (
                                <ul className="picker-grid">
                                    {filteredExercises.map((exercise) => (
                                        <li key={exercise.id}>
                                            <button
                                                type="button"
                                                className={`picker-card${
                                                    selectedExercise?.id === exercise.id ? ' selected' : ''
                                                }`}
                                                onClick={() => handleExerciseSelect(exercise)}
                                                aria-pressed={selectedExercise?.id === exercise.id}
                                            >
                                                <span className="picker-card-name">{exercise.name}</span>
                                                <span className="picker-card-category">
                                                    {exercise.category}
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        )}

                        {(editingExercise || selectedExercise) && (
                            <div className="prescription-fields">
                                <p className="kicker">
                                    Prescription —{' '}
                                    {editingExercise
                                        ? editingExercise.exerciseName
                                        : selectedExercise.name}
                                </p>

                                <div className="field-row">
                                    <FormField
                                        name="series"
                                        label="Series"
                                        value={exerciseEntry.series}
                                        onChange={handleEntryChange}
                                        error={fieldErrors.series}
                                        hint="A, A1, A2…"
                                    />
                                    <FormField
                                        name="sets"
                                        label="Sets"
                                        type="number"
                                        value={exerciseEntry.sets}
                                        onChange={handleEntryChange}
                                        error={fieldErrors.sets}
                                    />
                                    <FormField
                                        name="reps"
                                        label="Reps"
                                        type="number"
                                        value={exerciseEntry.reps}
                                        onChange={handleEntryChange}
                                        error={fieldErrors.reps}
                                    />
                                </div>

                                <div className="field-row">
                                    <FormField
                                        name="tempo"
                                        label="Tempo"
                                        value={exerciseEntry.tempo}
                                        onChange={handleEntryChange}
                                        error={fieldErrors.tempo}
                                        hint="ecc-pause-con-pause, X for explosive"
                                    />
                                    <FormField
                                        name="rest"
                                        label="Rest (seconds)"
                                        type="number"
                                        value={exerciseEntry.rest}
                                        onChange={handleEntryChange}
                                        error={fieldErrors.rest}
                                    />
                                </div>

                                {/* Intensity is optional — accessory work is legitimately
                                    prescribed by sets and reps alone (PRESC-API-3) — so it sits
                                    in its own secondary group. */}
                                <fieldset className="intensity-group">
                                    <legend className="kicker">Intensity — optional</legend>
                                    <div className="field-row">
                                        <FormField
                                            name="load"
                                            label={loadLabel(unit)}
                                            type="number"
                                            value={exerciseEntry.load}
                                            onChange={handleEntryChange}
                                            error={fieldErrors.load}
                                            hint="0 for bodyweight, negative for assisted"
                                        />
                                        <FormField
                                            name="percentOneRM"
                                            label="% of 1RM"
                                            type="number"
                                            value={exerciseEntry.percentOneRM}
                                            onChange={handleEntryChange}
                                            error={fieldErrors.percentOneRM}
                                        />
                                    </div>
                                    <div className="field-row">
                                        <FormField
                                            name="rpe"
                                            label="RPE"
                                            type="number"
                                            value={exerciseEntry.rpe}
                                            onChange={handleEntryChange}
                                            error={fieldErrors.rpe}
                                            hint="1–10, half steps"
                                        />
                                        <FormField
                                            name="rir"
                                            label="RIR"
                                            type="number"
                                            value={exerciseEntry.rir}
                                            onChange={handleEntryChange}
                                            error={fieldErrors.rir}
                                            hint="Reps in reserve"
                                        />
                                    </div>
                                </fieldset>

                                <label className="field-label" htmlFor="entry-notes">
                                    Notes
                                    <textarea
                                        id="entry-notes"
                                        name="notes"
                                        className="field notes-field"
                                        value={exerciseEntry.notes}
                                        onChange={handleEntryChange}
                                    />
                                </label>
                            </div>
                        )}

                        <div className="form-actions">
                            <button
                                className="btn-primary"
                                onClick={handleExerciseSubmit}
                                disabled={(!editingExercise && !selectedExercise) || isSubmitting}
                            >
                                {isSubmitting
                                    ? 'Saving…'
                                    : editingExercise
                                      ? 'Save changes'
                                      : 'Save exercise'}
                            </button>
                            <button className="btn-ghost" onClick={closeExerciseForm}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {selectedVideo && (
                <VideoModal
                    isOpen
                    videoUrl={selectedVideo}
                    onClose={() => setSelectedVideo(null)}
                />
            )}
        </BaseLayout>
    );
};

/**
 * A labelled field with an inline error. Extracted because the form has eleven
 * of them and the label/input/error wiring must stay consistent — in particular
 * the htmlFor/id pairing, without which the label is decorative.
 */
const FormField = ({ name, label, value, onChange, error, type = 'text', hint }) => {
    const id = `entry-${name}`;
    const hintId = `${id}-hint`;
    const errorId = `${id}-error`;

    // The hint and error sit outside the <label> on purpose. Nested inside, their
    // text becomes part of the field's accessible name — "Series" would be
    // announced as "Series A, A1, A2…". As descriptions they are announced
    // separately, after the name.
    const describedBy = [error ? errorId : null, hint ? hintId : null]
        .filter(Boolean)
        .join(' ');

    return (
        <div className="field-label">
            <label htmlFor={id}>{label}</label>
            <input
                id={id}
                name={name}
                type={type}
                className={`field${error ? ' field-invalid' : ''}`}
                value={value}
                onChange={onChange}
                aria-invalid={error ? 'true' : undefined}
                aria-describedby={describedBy || undefined}
            />
            {hint && !error && (
                <span className="field-hint" id={hintId}>
                    {hint}
                </span>
            )}
            {error && (
                <span className="field-error" id={errorId}>
                    {error}
                </span>
            )}
        </div>
    );
};

export default EditProgram;
